import {Box, Grid, SelectChangeEvent} from '@mui/material';
import {t} from 'i18next';
import {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  getPermissionsList,
  getProjectByProjectId,
} from '../../../api/ClientManagementService';
import {
  ECA_PERMISSION_CODES_CONSTANTS,
  EVIDENCE_SOURCE_MANAGEMENT,
  PERMISSION_GROUP_CATEGORY,
} from '../../../constants/constants';
import {useModal} from '../../../contexts/modalContext/ModalContext';
import {useToast} from '../../../contexts/toastContext/ToastContext';
import {
  getECAPermissions,
  setPermissions,
} from '../../../redux/slices/permissionsSlice';
import {Permission} from '../../../types/permission-group/permission';
import {PermissionGroup} from '../../../types/permission-group/permissionGroup';
import {ClientProject} from '../../../types/project-type/projectDetailsType';
import AurButton from '../../common/aur-button/AurButton';
import AurSelectDropdown from '../../common/aur-select-dropdown/AurSelectDropdown';
import {ISelectItem} from '../../common/aur-select-dropdown/types';
import AurProjectSelect from '../../common/aur-select/AurProjectSelect';
import {ProjectDetails, Workspace} from '../../pages/user-management/models';
import AurWorkspaceChipList from '../aur-workspace-chip/AurWorkspaceChipList';
import {getWorkspacesWithDisplayValue} from '../../pages/user-management/tabs/utils';
import {isEmpty} from 'lodash';
import {EstimatedBudgetValue} from '../../permission-groups/components/EstimatedBudgetView';
import AurIcon, {IconNames} from '../../common/aur-icon/AurIcon';
import {RootState} from '../../../redux/store';
import {EvidenceSourceManageValue} from '../../permission-groups/components/EvidenceSourceManageView';
import AurPermissionGroupSelect from '../../common/aur-select/AurPermissionGroupSelect';
import {ItemType} from '../../pages/user-management/tabs/AddClientInfo';
import {AurClientProjectSectionHeader} from '../../common/aur-page-header/AurClientProjectHeader';

export interface ClientProjectSelectionProps {
  clientProjects: ClientProject[];
  clientHCProjects: ClientProject[];
  onAddThisProjectClick?: (
    project: ProjectDetails,
    prevProjectId?: string
  ) => void;
  onCancelClick?: () => void;
  unsavedProjects?: ProjectDetails[];
  defaultSelectedProject?: ProjectDetails;
  sectionTitleHidden?: boolean;
  cancelButtonHidden?: boolean;
  isEditDisabled?: boolean;
  isAdmin?: boolean;
  showHCProjects?: boolean;
  showPermissionGroupDropdown?: boolean;
  selectedPermissionGroup?: PermissionGroup | null;
  selectedClientId?: string;
  hideProjectDropdown?: boolean;
  selectedClientBudgetEstimatedPermisionEnable?: boolean;
  projectTitle?: string;
  deleteProjectButtonClicked?: () => void;
  buttonLabel?: string;
  isCreateMode?: boolean;
  setCurrentProject?: (project: ProjectDetails) => void;
  projectItemsVisible?: number;
  anchorBottonPosition?: boolean;
  clientInfoData?: {
    clientId?: string;
    clientName?: string;
    role?: string;
    project?: string;
    workspaces?: ISelectItem[];
    permissionGroup?: string;
  };
  onPermissionChange?: (value: string) => void;
  setIsWorkspace?: (value: boolean) => void;
  onCreateNewPermissionGroup?: (clientId: string) => void;
  permissionOptionList?: ItemType[];
}

export const userRoleSelector = (state: RootState) => state.userRole;

const ClientProjectSelection = (props: ClientProjectSelectionProps) => {
  const {
    clientProjects,
    clientHCProjects,
    onAddThisProjectClick,
    unsavedProjects = [],
    onCancelClick,
    defaultSelectedProject,
    sectionTitleHidden = false,
    cancelButtonHidden = false,
    isEditDisabled = false,
    isAdmin = false,
    showHCProjects = false,
    showPermissionGroupDropdown,
    selectedPermissionGroup,
    selectedClientId,
    hideProjectDropdown,

    deleteProjectButtonClicked,
    buttonLabel = t('buttons.saveChanges'),
    isCreateMode = false,
    setCurrentProject,
    onCreateNewPermissionGroup,
    onPermissionChange,
    permissionOptionList,
    setIsWorkspace,
  } = props;

  const {showToast} = useToast();
  const [selectedProjectId, setSelectedProjectId] = useState(
    defaultSelectedProject?.projectId
  );
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<ISelectItem[]>(
    (getWorkspacesWithDisplayValue(
      defaultSelectedProject?.workspaces
    ) as unknown as ISelectItem[]) ?? []
  );
  const [workspaceOptions, setWorkspaceOptions] = useState<ISelectItem[]>([]);
  const [addButtonEnabled, setAddButtonEnabled] = useState(false);
  const [, setIsEcaEnabled] = useState(false);
  const [isProjectLevelEcaEnabled, setIsProjectLevelEcaEnabled] =
    useState(false);
  const [, setIsEditEcaPermissions] = useState(false);
  const {showModal} = useModal();
  const [permissionKey, setPermissionKey] = useState<string | undefined>('');
  const [createDocumentEnabled, setCreateDocumentEnabled] = useState<boolean>(
    !!selectedPermissionGroup?.permissions.find(
      (perm) =>
        perm.permissionCode ===
        ECA_PERMISSION_CODES_CONSTANTS.ECA_CREATE_DOCUMENT
    )
  );

  const [budgetEstimated, setBudgetEstimated] = useState<EstimatedBudgetValue>({
    rightToViewEstimatedBudget:
      defaultSelectedProject?.permissionCodeBudgetConsumption,
    showBudgetAlerts: defaultSelectedProject?.permissionCodeBudgetAlert,
  });

  const [evidenceSourcePermissions, setEvidenceSourcePermissions] =
    useState<EvidenceSourceManageValue>({
      manageProjectEvidence: false,
      uploadRights: false,
      initiateProcessingRights: false,
    });

  const [permissionGroup, setPermissionGroup] = useState<string | undefined>(
    defaultSelectedProject?.permissionGroupId
  );

  const effectivePermissionGroupId =
    selectedPermissionGroup?.permissionGroupId ?? permissionGroup ?? '';

  useEffect(() => {
    if (defaultSelectedProject?.permissionGroupId) {
      setPermissionGroup(defaultSelectedProject.permissionGroupId);
    }
  }, [defaultSelectedProject?.permissionGroupId]);

  useEffect(() => {
    const fromParent = selectedPermissionGroup?.permissionGroupId;
    if (fromParent && fromParent !== permissionGroup) {
      setPermissionGroup(fromParent);
    }
  }, [selectedPermissionGroup?.permissionGroupId, permissionGroup]);

  useEffect(() => {
    if (!defaultSelectedProject?.workspaces || workspaceOptions.length === 0) {
      return;
    }
    const selected = defaultSelectedProject.workspaces
      .map((ws) =>
        workspaceOptions.find((opt) => opt.workspaceId === ws.workspaceId)
      )
      .filter(Boolean) as ISelectItem[];
    if (selected.length > 0) {
      setSelectedWorkspaces(selected);
    }
  }, [defaultSelectedProject?.projectId, workspaceOptions]);
  useEffect(() => {
    if (defaultSelectedProject) {
      setBudgetEstimated({
        rightToViewEstimatedBudget:
          defaultSelectedProject.permissionCodeBudgetConsumption ?? false,
        showBudgetAlerts:
          defaultSelectedProject.permissionCodeBudgetAlert ?? false,
      });
    }
  }, [defaultSelectedProject]);

  useEffect(() => {
    if (!defaultSelectedProject) return;
    setEvidenceSourcePermissions({
      manageProjectEvidence:
        defaultSelectedProject.manageProjectEvidence ??
        defaultSelectedProject.projectPermissions?.some(
          (p) =>
            p.permissionCode === EVIDENCE_SOURCE_MANAGEMENT.STAGING_AREA_ACCESS
        ),
      uploadRights:
        defaultSelectedProject.uploadRights ??
        defaultSelectedProject.projectPermissions?.some(
          (p) =>
            p.permissionCode ===
            EVIDENCE_SOURCE_MANAGEMENT.EVIDENCE_SOURCE_UPLOAD_ACCESS
        ),
      initiateProcessingRights:
        defaultSelectedProject.initiateProcessingRights ??
        defaultSelectedProject.projectPermissions?.some(
          (p) =>
            p.permissionCode ===
            EVIDENCE_SOURCE_MANAGEMENT.INITIATE_PROCESSING_ACCESS
        ),
    });
  }, [defaultSelectedProject]);
  useEffect(() => {
    if (selectedProjectId) {
      getWorkspaceOptions(selectedProjectId);
    }
  }, [selectedProjectId]);
  useEffect(() => {
    setIsEcaEnabled(false);
    setIsProjectLevelEcaEnabled(false);
    setIsEditEcaPermissions(false);
  }, [selectedClientId, selectedPermissionGroup, selectedProjectId]);

  useEffect(() => {
    setSelectedProjectId('');
    // setSelectedWorkspaces([]);
  }, [selectedClientId]);

  useEffect(() => {
    setIsWorkspace?.(!!selectedWorkspaces?.length);
  }, [selectedWorkspaces]);

  useEffect(() => {
    isAddButtonEnable();
  }, [selectedProjectId, selectedWorkspaces]);

  const isAddButtonEnable = () => {
    if (!selectedProjectId) setAddButtonEnabled(false);
    else setAddButtonEnabled(true);
  };
  const dispatch = useDispatch();
  const ecaPermissions = useSelector(getECAPermissions);
  const allPermissions = useSelector(
    (state: RootState) => state.permissions.allPermissions
  );
  const fetchPermissions = async () => {
    try {
      const result = await getPermissionsList();
      dispatch(setPermissions(result?.data?.data));
    } catch {
      showToast(
        t('toast.failedToLoadPermissions'),
        'error',
        undefined,
        'center'
      );
    }
  };
  useEffect(() => {
    fetchPermissions();
    handleProjectChange(selectedProjectId ?? '');
  }, []);
  useEffect(() => {
    if (
      defaultSelectedProject?.projectPermissions?.some((permission) =>
        permission.permissionCode.includes(
          ECA_PERMISSION_CODES_CONSTANTS.ECA_NONE
        )
      )
    ) {
      setIsEcaEnabled(false);
    } else if (
      isProjectLevelEcaEnabled &&
      selectedPermissionGroup?.permissions.some((permission) =>
        permission.permissionCode.includes(ECA_PERMISSION_CODES_CONSTANTS.ECA)
      ) &&
      !selectedPermissionGroup?.permissions.some((permission) =>
        permission.permissionCode.includes(
          ECA_PERMISSION_CODES_CONSTANTS.ECA_NONE
        )
      )
    ) {
      setIsEcaEnabled(true);
    } else {
      setIsEcaEnabled(false);
    }
  }, [selectedPermissionGroup, isProjectLevelEcaEnabled]);

  useEffect(() => {
    const permissionsArray =
      defaultSelectedProject?.projectPermissions ??
      selectedPermissionGroup?.permissions;

    const projectPermissions = permissionsArray ?? [];
    if (projectPermissions.length > 0) {
      let hasEcaPermissionCode;
      let hasEcaCreateDocument = false;

      projectPermissions?.forEach((item) => {
        if (item?.type === ECA_PERMISSION_CODES_CONSTANTS.ECA) {
          if (
            item.permissionCode ===
            ECA_PERMISSION_CODES_CONSTANTS.ECA_CREATE_DOCUMENT
          ) {
            hasEcaCreateDocument = true;
          } else {
            hasEcaPermissionCode = item.permissionId;
          }
        }
      });

      if (!isEmpty(hasEcaPermissionCode)) {
        setIsEcaEnabled(true);
        setPermissionKey(hasEcaPermissionCode);
        setCreateDocumentEnabled(hasEcaCreateDocument);
      }
    }
  }, [defaultSelectedProject, selectedPermissionGroup]);

  const getWorkspaceOptions = async (selectedProjectId: string) => {
    try {
      const response = await getProjectByProjectId(selectedProjectId);
      const workspacesData = getWorkspacesWithDisplayValue(
        response.data?.data?.workspaces
      );
      setWorkspaceOptions(workspacesData as unknown as ISelectItem[]);
      if (response?.data?.data.ecaEnabled) {
        setIsProjectLevelEcaEnabled(true);
      } else {
        setIsProjectLevelEcaEnabled(false);
      }
    } catch {
      return [];
    }
  };

  const resetBudgetEstimated = useCallback(() => {
    setBudgetEstimated({
      rightToViewEstimatedBudget: false,
      showBudgetAlerts: false,
    });
  }, []);

  const handleProjectChange = async (selectedProjectId: string) => {
    setSelectedProjectId(selectedProjectId);
  };

  /*
  In this below function we are setting ECA permissions for project based on selected Permission Group.
  1. If Permission group has Admin and Create Document permissions, then project level will have all the permissions
  2. If Permission group has Non-Admin and Create Document permissions, then on project levelapart 
  from admin all the permissions will be there
  3. If Permission group has Non-Admin and Can't Create Document permissions, then on project level apart 
  from admin and document creation, all the permissions will be there
  4. If Permission group has Admin and Can't Create Document permissions, then on project level apart 
  from document creation, all the permissions will be there
  */

  const getProjectLevelPermissions = (): Permission[] => {
    const permsissionArray: Permission[] = [];
    if (
      ecaPermissions.find(
        (permission) => permission.permissionId === permissionKey
      )
    ) {
      permsissionArray.push(
        ecaPermissions.find(
          (permission) => permission.permissionId === permissionKey
        ) as Permission
      );
    }

    if (createDocumentEnabled) {
      permsissionArray.push(
        ecaPermissions.find(
          (permission) =>
            permission.permissionCode ===
            ECA_PERMISSION_CODES_CONSTANTS.ECA_CREATE_DOCUMENT
        ) as Permission
      );
    }
    return permsissionArray;
  };

  const getProjectDetails = () => {
    if (selectedProjectId) {
      const project = isAdmin
        ? clientHCProjects.find(
            (x) => x.projectId.toLowerCase() === selectedProjectId.toLowerCase()
          )
        : clientProjects.find(
            (x) => x.projectId.toLowerCase() === selectedProjectId.toLowerCase()
          );

      const updatedWorkspaces = selectedWorkspaces as unknown as Workspace[];
      if (hideProjectDropdown) {
        const deletedWorkspaces: Workspace[] =
          defaultSelectedProject?.workspaces
            .filter(
              (w) =>
                !selectedWorkspaces.find(
                  (pw) => pw.workspaceId == w.workspaceId
                )
            )
            .map((dw) => {
              return {...dw, isDeleted: true};
            }) ?? [];
        if (deletedWorkspaces?.length > 0)
          updatedWorkspaces.push(...deletedWorkspaces);
      }
      const ecaPermissions = getProjectLevelPermissions();
      const finalPermissions: Permission[] = [...ecaPermissions];

      // Add Evidence Permissions
      if (evidenceSourcePermissions.manageProjectEvidence) {
        const perm = allPermissions.find(
          (p) =>
            p.permissionCode === EVIDENCE_SOURCE_MANAGEMENT.STAGING_AREA_ACCESS
        );
        perm && finalPermissions.push(perm);
      }

      if (evidenceSourcePermissions.uploadRights) {
        const perm = allPermissions.find(
          (p) =>
            p.permissionCode ===
            EVIDENCE_SOURCE_MANAGEMENT.EVIDENCE_SOURCE_UPLOAD_ACCESS
        );
        perm && finalPermissions.push(perm);
      }

      if (evidenceSourcePermissions.initiateProcessingRights) {
        const perm = allPermissions.find(
          (p) =>
            p.permissionCode ===
            EVIDENCE_SOURCE_MANAGEMENT.INITIATE_PROCESSING_ACCESS
        );
        perm && finalPermissions.push(perm);
      }
      const projectDetails: ProjectDetails = {
        projectId: selectedProjectId,
        highlyConfidential: project?.highlyConfidential,
        projectName: project?.projectName as string,
        dataCenterName: project?.dataCenterName as string,
        domainCountryCode: project?.domainCountryCode as string,
        projectPermissions: finalPermissions,
        workspaces: updatedWorkspaces,
        permissionGroupId:
          selectedPermissionGroup?.permissionGroupId ?? permissionGroup ?? '',
        permissionGroup:
          selectedPermissionGroup?.name ??
          permissionOptionList?.find(
            (item) => item.ItemKey == effectivePermissionGroupId
          )?.ItemName ??
          '',
        countryName: project?.countryName as string,
        manageProjectEvidence: evidenceSourcePermissions.manageProjectEvidence,
        uploadRights: evidenceSourcePermissions.uploadRights,
        initiateProcessingRights:
          evidenceSourcePermissions.initiateProcessingRights,
      };

      projectDetails.permissionCodeBudgetConsumption =
        budgetEstimated.rightToViewEstimatedBudget;
      projectDetails.permissionCodeBudgetAlert =
        budgetEstimated.showBudgetAlerts;

      return projectDetails;
    }
  };

  useEffect(() => {
    if (isCreateMode) {
      setSelectedProjectId('');
      resetBudgetEstimated();
      setSelectedWorkspaces([]);
    }
  }, [isAdmin, showHCProjects, isCreateMode]);

  useEffect(() => {
    if (selectedProjectId && isCreateMode && setCurrentProject) {
      const project = getProjectDetails();
      project && setCurrentProject(project);
    }
  }, [
    selectedProjectId,
    selectedWorkspaces.length,
    permissionKey,
    createDocumentEnabled,
    evidenceSourcePermissions,
    permissionGroup,
  ]);

  const handleAddThisProjectClick = () => {
    if (selectedProjectId && (isAdmin || effectivePermissionGroupId)) {
      const projectDetails = getProjectDetails();
      onAddThisProjectClick &&
        projectDetails &&
        onAddThisProjectClick(
          projectDetails,
          defaultSelectedProject?.projectId
        );
    }
  };

  const handleCancelClick = () => {
    if (isFormModified()) {
      showModal({
        title: t('addEditTeamMember.cancelEditPopup.title'),
        description: t('addEditTeamMember.cancelEditPopup.content'),
        labelOkButton: t('addEditTeamMember.cancelEditPopup.buttonYes'),
        labelCancelButton: t('addEditTeamMember.cancelEditPopup.buttonCancel'),
        onOk: onCancelClick,
        buttonStyle: 'sideBySide',
      });
      return;
    }

    onCancelClick && onCancelClick();
  };

  const isFormModified = () => {
    if (hideProjectDropdown) {
      return (
        selectedProjectId !== defaultSelectedProject?.projectId ||
        selectedWorkspaces.length !==
          (defaultSelectedProject
            ? defaultSelectedProject?.workspaces.length
            : 0) ||
        selectedWorkspaces?.some(
          (workspace, index) =>
            workspace.workspaceId !==
            defaultSelectedProject?.workspaces[index]?.workspaceId
        )
      );
    } else {
      return !isEmpty(selectedProjectId) || !isEmpty(selectedWorkspaces);
    }
  };

  useEffect(() => {
    typeof hideProjectDropdown === 'boolean' &&
      !hideProjectDropdown &&
      setSelectedProjectId('');
  }, [clientHCProjects, clientProjects]);

  return (
    <Box>
      <Grid
        container
        className="add-client-info-container"
        sx={{marginTop: '24px'}}
        spacing={3}
      >
        {!sectionTitleHidden && (
          <AurClientProjectSectionHeader
            title={t('addEditTeamMember.projectAccess')}
            subheader={t('addEditTeamMember.projectAccessSubheader')}
            style={{paddingTop: '0'}}
          />
        )}
        {!hideProjectDropdown && (
          <Grid
            item
            sm={12}
            md={12}
            className="form-content remove-padding-left"
          >
            <AurProjectSelect
              field={{
                value: selectedProjectId,
                onChange: (event: SelectChangeEvent<string>) => {
                  setSelectedProjectId(event.target.value);
                  resetBudgetEstimated();
                  setSelectedWorkspaces([]);
                },
              }}
              fieldState={{}}
              showHCProjects={showHCProjects}
              isAdmin={isAdmin}
              disabled={isEditDisabled}
              clientProjects={clientProjects}
              clientHCProjects={clientHCProjects}
              unsavedProjects={unsavedProjects}
              projectItemsVisible={props.projectItemsVisible}
              anchorBottonPosition={props.anchorBottonPosition}
            />
          </Grid>
        )}
        {!showHCProjects && (
          <Grid
            item
            sm={12}
            md={12}
            className="permission-selection remove-padding-left"
          >
            <AurPermissionGroupSelect
              disabled={!selectedProjectId}
              items={permissionOptionList ?? []}
              value={effectivePermissionGroupId}
              onChange={(value) => {
                setPermissionGroup(value);
                onPermissionChange?.(value);
              }}
              label={t('addEditTeamMember.form.permissionGroup')}
              clientId={selectedClientId}
              onCreateNewPermissionGroup={onCreateNewPermissionGroup}
              category={PERMISSION_GROUP_CATEGORY.PROJECT}
              subheaderLabel={t('common.projectPermGroupSubheader')}
            />
          </Grid>
        )}
        {!isAdmin && !showPermissionGroupDropdown ? (
          <Grid
            item
            sm={12}
            md={12}
            className="form-content dropdown-with-chips remove-padding-left"
          >
            <AurSelectDropdown
              // key={`${selectedProjectId}-${workspaceOptions.length}`}
              label={t('addEditTeamMember.form.workspaces')}
              items={workspaceOptions}
              defaultSelection={[
                ...(selectedWorkspaces.map((w) => {
                  return {
                    workspaceId: w.workspaceId,
                    workspaceName: w.workspaceName,
                    workspaceCode: w.workspaceCode,
                    workspaceDisplay: w.workspaceDisplay,
                  };
                }) ?? []),
              ]}
              itemKey="workspaceId"
              itemDisplay="workspaceDisplay"
              dropdownLabel={t('addEditTeamMember.form.workspaces')}
              search={false}
              selectAll={false}
              submitButtonText={t('common.select')}
              labelId={'project-group'}
              onSelectionChange={(selectedItems) => {
                setSelectedWorkspaces(selectedItems || []);
              }}
              onClearClicked={() => {
                setSelectedWorkspaces([]);
              }}
              dataTestId="aur-test-workspaces-dropdown"
              disabled={isEditDisabled}
              helperText={
                isEditDisabled
                  ? t('addEditTeamMember.form.workspacesHelperText')
                  : ''
              }
              helperTextClass="helptext-color"
            />
            {selectedWorkspaces.length > 0 && (
              <AurWorkspaceChipList
                items={selectedWorkspaces || []}
                itemKey="workspaceId"
                itemLabel="workspaceDisplay"
                onDelete={(deletedItem, remainingItems) => {
                  setSelectedWorkspaces(remainingItems ?? []);
                }}
              />
            )}
          </Grid>
        ) : (
          <></>
        )}
      </Grid>

      <Grid container className="add-client-info-container" spacing={3}>
        <Grid
          item
          sm={12}
          md={12}
          className="text-right user-form remove-padding-left"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: `${hideProjectDropdown ? 'space-between' : 'end'}`,
          }}
        >
          {hideProjectDropdown && (
            <AurButton
              ariaLabel={t('addEditTeamMember.form.removeProjectButton')}
              disabled={false}
              type="text"
              startIcon={<AurIcon icon={IconNames.Remove} size="small" />}
              onClick={() =>
                deleteProjectButtonClicked && deleteProjectButtonClicked()
              }
              dataTestId="remove-project"
              label={t('addEditTeamMember.form.removeProjectButton')}
              size="Medium"
            />
          )}
          <Box sx={{display: 'flex', flexDirection: 'row', gap: '16px'}}>
            {!cancelButtonHidden && (
              <AurButton
                classNames="typo-text buttonLarge_size submit-btn"
                type="exit"
                onClick={handleCancelClick}
                label={t('buttons.dontSaveUpdate')}
                dataTestId="cancel-changes"
              />
            )}

            {!isCreateMode && (
              <AurButton
                classNames="typo-text buttonLarge_size submit-btn"
                type="continue"
                disabled={
                  !addButtonEnabled ||
                  isEditDisabled ||
                  (!isAdmin && !effectivePermissionGroupId)
                }
                label={buttonLabel}
                dataTestId="save-changes"
                onClick={handleAddThisProjectClick}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClientProjectSelection;
